import { Container, Group, Box, Image } from "@mantine/core";
import { useCallback, useMemo, useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { persistirVotos } from "@/modules/projetos/firestore";
import { Parameters_ } from "@/src/routes/votar-empresa-inspiradora-externo-page/components/VotarEmpresaInspiradoraExternoPage";
import {
  getCategoriasVotadas,
  salvarCategoriaVotada,
  verificarELimparCacheSeNecessario,
  marcarVotoConfirmado
} from "@/utils/votos-locais";
import Pagina1 from "./BemVindos";
import PaginaConfirmacao from "./PaginaConfirmacao";
import PaginaFinal from "./PaginaFinal";
import Pagina2 from "./SelecaoCategoria";
import Pagina3 from "./SelecaoEmpresa";
import Pagina4 from "./SubmeterVoto";

const STEP_FINAL = 5;
const TOTAL_PASSOS = 6;
const TODAS_CATEGORIAS = ["Cooperativas", "Serviços", "Indústria", "Tecnologia e Comunicação"];

type Voto = {
  categoria: string;
  empresa: string;
};

const Wizard = () => {
  const { licenciadaId, empresaId = "", questionarioId = "" } = useParams<Parameters_>();
  const [stepAtual, setStepAtual] = useState(0);
  const [votos, setVotos] = useState<Voto[]>([]);
  const [categoriaSelecionada, setCategoriaSelecionada] = useState<string | null>(null);
  const [categoriasVotadas, setCategoriasVotadas] = useState<string[]>([]);

  // Efeito para inicializar sessão
  useEffect(() => {
    verificarELimparCacheSeNecessario();
  }, []);

  const categoriasDisponiveis = useMemo(() => {
    const locais = getCategoriasVotadas();
    return TODAS_CATEGORIAS.filter((cat) => !locais.includes(cat) && !categoriasVotadas.includes(cat));
  }, [categoriasVotadas]);

  const avancar = useCallback(
    async (fromPage: string) => {
      if (categoriasDisponiveis.length === 0) {
        setStepAtual(STEP_FINAL);
        return;
      }

      // Quando confirma os votos na página de confirmação
      if (fromPage === "PaginaConfirmacao") {
        // Marcar que o voto foi confirmado
        marcarVotoConfirmado();

        try {
          await persistirVotos(licenciadaId || "", empresaId, questionarioId, votos);
        } catch (error) {
          console.error("Erro ao persistir votos:", error);
        }
      }

      setStepAtual((previous) => Math.min(previous + 1, TOTAL_PASSOS - 1));
    },
    [categoriasDisponiveis, licenciadaId, empresaId, questionarioId, votos]
  );

  const voltar = useCallback(() => {
    setStepAtual((previous) => Math.max(previous - 1, 0));
  }, []);

  const irParaConfirmacao = useCallback(() => {
    setStepAtual(STEP_FINAL - 1);
  }, []);

  const handleSelecionarCategoria = useCallback(
    (categoria: string) => {
      setCategoriaSelecionada(categoria);
      void avancar("SelecaoCategoria");
    },
    [avancar]
  );

  const handleVotarNovamente = useCallback(() => {
    setCategoriaSelecionada(null);
    setStepAtual(0);
    void avancar("VotarNovamente");
  }, [avancar]);

  const handleSubmeterVoto = useCallback(
    ({ empresa, categoria }: { empresa: string; categoria: string }) => {
      setVotos((previous) => [...previous, { categoria, empresa }]);
      setCategoriasVotadas((previous) => [...previous, categoria]);
      salvarCategoriaVotada(categoria);
      void avancar("SubmeterVoto");
    },
    [avancar, setVotos, setCategoriasVotadas]
  );

  const handleFinalizar = useCallback(() => {
    void avancar("Finalizar");
  }, [avancar]);

  const handleAvancarBemVindos = useCallback(() => void avancar("BemVindos"), [avancar]);
  const handleConfirmarPaginaConfirmacao = useCallback(() => void avancar("PaginaConfirmacao"), [avancar]);

  const paginas = useMemo(
    () => [
      <Pagina1 key="pagina-1" onAvancar={handleAvancarBemVindos} />,
      <Pagina2
        key="pagina-2"
        onSelecionarCategoria={handleSelecionarCategoria}
        categoriasDisponiveis={categoriasDisponiveis}
        onConfirmarVotos={irParaConfirmacao}
        votos={votos}
      />,
      <Pagina3
        key="pagina-3"
        categoria={categoriaSelecionada ?? ""}
        onSubmeterVoto={handleSubmeterVoto}
        onVoltar={voltar}
      />,
      <Pagina4
        key="pagina-4"
        categoriaVotada={categoriaSelecionada ?? ""}
        onVotarNovamente={handleVotarNovamente}
        onFinalizar={handleFinalizar}
      />,
      <PaginaConfirmacao key="pagina-confirmacao" votos={votos} onConfirmar={handleConfirmarPaginaConfirmacao} />,
      <PaginaFinal key="pagina-final" />
    ],
    [
      handleAvancarBemVindos,
      handleSelecionarCategoria,
      categoriasDisponiveis,
      categoriaSelecionada,
      handleSubmeterVoto,
      voltar,
      handleVotarNovamente,
      handleFinalizar,
      votos,
      handleConfirmarPaginaConfirmacao
    ]
  );

  const paginaAtual = paginas[stepAtual];

  return (
    <Container
      size="sm"
      py="xl"
      style={{
        backgroundColor: "white",
        borderRadius: "12px",
        boxShadow: "0 10px 25px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)"
      }}
    >
      <Group justify="space-between" align="center" w="100%">
        <Image
          src="https://firebasestorage.googleapis.com/v0/b/acelera-inova.appspot.com/o/assets%2Fpei_preta.png?alt=media&token=1c799332-0113-4947-9c18-fba8a9963c8a"
          alt="Logo PEI"
          w="45%"
          maw={240}
        />
        <Image
          src="https://firebasestorage.googleapis.com/v0/b/acelera-inova.appspot.com/o/assets%2FHazeP.png?alt=media&token=85a14c89-8b01-44b0-9e14-5991c3d46250"
          alt="Logo Haze"
          w="45%"
          maw={240}
        />
      </Group>
      <Box>{paginaAtual}</Box>
      <Group justify="center" align="center" w="100%" mt="-md">
        <Image
          src="https://firebasestorage.googleapis.com/v0/b/acelera-inova.appspot.com/o/assets%2Flogo_synapse.png?alt=media&token=0105d8da-c9a7-4b67-806e-f356817ca7d5"
          alt="Logo Synapse"
          w="100%"
          maw={500}
        />
      </Group>
    </Container>
  );
};

export default Wizard;
